import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchAuditorControlInventory,
  fetchAuditorExceptionReport,
  fetchAuditorIssueLedger,
  fetchAuditorProcessChartingStatus,
  fetchAuditorRelianceEligible,
  fetchAuditorWorkpapersPackage,
  type AuditorControlInventoryRow,
  type AuditorExceptionReportRow,
  type AuditorIssueLedgerRow,
  type AuditorProcessChartingStatusResponse,
  type AuditorRelianceEligibleRow,
  type AuditorWorkpapersPackageRow,
} from '../../request/analytics/auditorReports.ts';
import { analyticsAuditorQueryKeys } from '../../queries/analytics/keys.ts';

/**
 * React Query v5 hooks for the External Auditor read-only analytics
 * dashboard (Feature 5.3, reports #37-#41). Each report is its own
 * independent query so a single failing/slow widget doesn't block the rest
 * of the dashboard. No mutations — this persona is view-only.
 */

export function useAuditorControlInventory(): UseQueryResult<AuditorControlInventoryRow[], Error> {
  return useQuery({
    queryKey: analyticsAuditorQueryKeys.controlInventory(),
    queryFn: fetchAuditorControlInventory,
  });
}

/**
 * Report #38 — Workpapers Package. Parameterized by `controlId`; the query
 * stays disabled (and simply doesn't fire) until a control id is supplied,
 * since the backend requires `control_id` as a query param.
 */
export function useAuditorWorkpapersPackage(controlId: string | undefined): UseQueryResult<AuditorWorkpapersPackageRow[], Error> {
  return useQuery({
    queryKey: analyticsAuditorQueryKeys.workpapersPackage(controlId),
    queryFn: () => fetchAuditorWorkpapersPackage(controlId as string),
    enabled: !!controlId,
  });
}

export function useAuditorExceptionReport(): UseQueryResult<AuditorExceptionReportRow[], Error> {
  return useQuery({
    queryKey: analyticsAuditorQueryKeys.exceptionReport(),
    queryFn: fetchAuditorExceptionReport,
  });
}

export function useAuditorIssueLedger(): UseQueryResult<AuditorIssueLedgerRow[], Error> {
  return useQuery({
    queryKey: analyticsAuditorQueryKeys.issueLedger(),
    queryFn: fetchAuditorIssueLedger,
  });
}

export function useAuditorRelianceEligible(): UseQueryResult<AuditorRelianceEligibleRow[], Error> {
  return useQuery({
    queryKey: analyticsAuditorQueryKeys.relianceEligible(),
    queryFn: fetchAuditorRelianceEligible,
  });
}

/** Process Charting Status (feedback round 2026-08) — charted vs. uncharted processes, by SOX cycle. */
export function useAuditorProcessChartingStatus(): UseQueryResult<AuditorProcessChartingStatusResponse, Error> {
  return useQuery({
    queryKey: analyticsAuditorQueryKeys.processChartingStatus(),
    queryFn: fetchAuditorProcessChartingStatus,
  });
}
